import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import TournamentBracket from '../components/TournamentBracket';
import TournamentLeaderboard from '../components/TournamentLeaderboard';
import { useUser } from '../context/UserContext';
import { Trophy, LayoutGrid } from 'lucide-react';

interface Tournament {
  id: string;
  title: string;
  status: 'upcoming' | 'active' | 'completed';
  is_private: boolean;
  is_shareable: boolean;
  max_participants: number;
  primary_color: string | null;
  secondary_color: string | null;
  logo_url: string | null;
  registration_locked: boolean;
  locked_participant_count: number | null;
  registration_close_date: string | null;
  prize_display?: string | null;
  background_type?: string | null;
  shader_preset?: string | null;
  sponsor_logos?: string[] | null;
  sponsor_links?: string[] | null;
  background_image_url?: string | null;
}

interface Participant {
  id: string;
  user_id: string;
  profile: {
    username: string;
    avatar_url: string | null;
    full_name?: string | null;
  };
  score: number | null;
  performance_id?: string | null;
  status?: string;
  created_at?: string;
  updated_at?: string | null;
}

type TabType = 'bracket' | 'leaderboard';

const SharedBracket: React.FC = () => {
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const navigate = useNavigate();
  const { user, isAdmin } = useUser();
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isParticipant, setIsParticipant] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('bracket');

  useEffect(() => {
    loadTournamentData();
  }, [tournamentId, user, isAdmin]);

  const loadTournamentData = async () => {
    if (!tournamentId) {
      setError('Invalid tournament link');
      setLoading(false);
      return;
    }

    try {
      const { data: tournamentData, error: tournamentError } = await supabase
        .from('tournaments')
        .select('id, title, status, is_private, is_shareable, max_participants, primary_color, secondary_color, logo_url, registration_locked, locked_participant_count, registration_close_date, prize_display, background_type, shader_preset, sponsor_logos, sponsor_links, background_image_url')
        .eq('id', tournamentId)
        .maybeSingle();

      if (tournamentError) throw tournamentError;

      if (!tournamentData) {
        setError('Tournament not found');
        setLoading(false);
        return;
      }

      if (!tournamentData.is_shareable && !isAdmin) {
        setError('This tournament bracket is not available for sharing');
        setLoading(false);
        return;
      }

      setTournament(tournamentData);

      if (tournamentData.is_private && !isAdmin) {
        if (!user) {
          setError('This is a private tournament. Please log in to view the bracket.');
          setLoading(false);
          return;
        }

        const { data: participantCheck } = await supabase
          .from('tournament_entries')
          .select('id')
          .eq('tournament_id', tournamentId)
          .eq('user_id', user.id)
          .maybeSingle();

        if (!participantCheck) {
          setError('This is a private tournament. You must be a participant to view the bracket.');
          setLoading(false);
          return;
        }

        setIsParticipant(true);
      }

      const { data: participantsData, error: participantsError } = await supabase
        .from('tournament_entries')
        .select(`
          id,
          user_id,
          score,
          performance_id,
          status,
          created_at,
          updated_at,
          profile:profiles!tournament_entries_user_id_fkey (
            username,
            avatar_url,
            full_name
          )
        `)
        .eq('tournament_id', tournamentId)
        .order('created_at');

      if (participantsError) throw participantsError;

      setParticipants(participantsData || []);
      setLoading(false);
    } catch (err) {
      console.error('Error loading tournament data:', err);
      setError('Failed to load tournament data');
      setLoading(false);
    }
  };

  const hasCustomBranding = tournament?.logo_url && tournament?.primary_color;
  const primaryColor = tournament?.primary_color || '#f97316';
  const secondaryColor = tournament?.secondary_color || '#1e3a5f';

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: secondaryColor }}
      >
        <div className="text-white text-xl">Loading bracket...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: hasCustomBranding ? secondaryColor : 'rgb(88, 28, 135)' }}
      >
        <div className="text-center">
          <div className="text-white text-xl mb-4">{error}</div>
          {!user && tournament?.is_private && (
            <button
              onClick={() => navigate('/login')}
              className="px-6 py-2 text-white rounded-lg transition-colors"
              style={{ backgroundColor: primaryColor }}
            >
              Log In
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!tournament) {
    return null;
  }

  const logoUrl = tournament.logo_url || '/TSL_Logo.png';

  const leaderboardEntries = participants.map(p => ({
    id: p.id,
    user_id: p.user_id,
    tournament_id: tournamentId!,
    performance_id: p.performance_id || null,
    status: (p.status || 'registered') as 'registered' | 'submitted' | 'evaluated',
    score: p.score,
    created_at: p.created_at || new Date().toISOString(),
    updated_at: p.updated_at || null,
    profile: {
      username: p.profile.username,
      full_name: p.profile.full_name || null,
      avatar_url: p.profile.avatar_url
    }
  }));

  if (hasCustomBranding) {
    return (
      <div
        className="min-h-screen flex flex-col"
        style={{ backgroundColor: secondaryColor }}
      >
        {/* Custom Branded Header */}
        <div
          className="py-6 px-6"
          style={{
            backgroundColor: secondaryColor,
            borderBottom: `1px solid ${primaryColor}30`
          }}
        >
          <div className="container mx-auto">
            <div className="flex items-start justify-between">
              <div className="flex flex-col">
                <img
                  src={logoUrl}
                  alt={tournament.title}
                  className="h-20 w-auto object-contain mb-3"
                />
                <h1
                  className="text-xl font-bold"
                  style={{ color: primaryColor }}
                >
                  {tournament.title}
                </h1>
              </div>
              <div
                className="px-3 py-1 rounded-full text-sm font-medium"
                style={{
                  backgroundColor: `${primaryColor}20`,
                  color: primaryColor
                }}
              >
                {tournament.status.charAt(0).toUpperCase() + tournament.status.slice(1)}
              </div>
            </div>
          </div>
        </div>

        {/* Custom Branded Tabs */}
        <div
          className="px-6"
          style={{ borderBottom: `1px solid ${primaryColor}20` }}
        >
          <div className="container mx-auto flex gap-1">
            <button
              onClick={() => setActiveTab('bracket')}
              className="flex items-center gap-2 px-6 py-3 font-medium transition-all border-b-2 -mb-px"
              style={{
                color: activeTab === 'bracket' ? primaryColor : 'rgba(255,255,255,0.5)',
                borderColor: activeTab === 'bracket' ? primaryColor : 'transparent'
              }}
            >
              <LayoutGrid size={18} />
              Bracket
            </button>
            <button
              onClick={() => setActiveTab('leaderboard')}
              className="flex items-center gap-2 px-6 py-3 font-medium transition-all border-b-2 -mb-px"
              style={{
                color: activeTab === 'leaderboard' ? primaryColor : 'rgba(255,255,255,0.5)',
                borderColor: activeTab === 'leaderboard' ? primaryColor : 'transparent'
              }}
            >
              <Trophy size={18} />
              Leaderboard
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {activeTab === 'bracket' ? (
            <TournamentBracket
              tournamentId={tournament.id}
              tournamentStatus={tournament.status}
              currentUserId={user?.id}
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
              registrationCloseDate={tournament.registration_close_date}
              registrationLocked={tournament.registration_locked}
              title={tournament.title}
              logoUrl={tournament.logo_url}
              prizeDisplay={tournament.prize_display}
              backgroundType={tournament.background_type}
              shaderPreset={tournament.shader_preset}
              sponsorLogos={tournament.sponsor_logos}
              sponsorLinks={tournament.sponsor_links}
              backgroundImageUrl={tournament.background_image_url}
            />
          ) : (
            <TournamentLeaderboard
              entries={leaderboardEntries}
              currentUserId={user?.id}
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
              isSharedView={true}
            />
          )}
        </div>

        {/* Custom Branded Footer */}
        <div
          className="py-3 px-6 text-center"
          style={{
            backgroundColor: secondaryColor,
            borderTop: `1px solid ${primaryColor}20`
          }}
        >
          <p className="text-white/40 text-sm">
            Powered by The Singing League
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-kod-purple-900 to-kod-purple-800">
      {/* Default TSL Header */}
      <div className="border-b border-kod-purple-600/30 py-4 px-6 bg-kod-purple-900/80">
        <div className="container mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src="/TSL_Logo.png"
              alt="The Singing League"
              className="h-16 w-auto object-contain"
            />
            <div>
              <h1 className="text-2xl font-bold text-white">{tournament.title}</h1>
              <p className="text-white/60 text-sm">The Singing League Tournament</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${
              tournament.status === 'upcoming' ? 'bg-blue-500/20 text-blue-300' :
              tournament.status === 'active' ? 'bg-green-500/20 text-green-300' :
              'bg-gray-500/20 text-gray-300'
            }`}>
              {tournament.status.charAt(0).toUpperCase() + tournament.status.slice(1)}
            </div>
          </div>
        </div>
      </div>

      {/* Default Tabs */}
      <div className="border-b border-kod-purple-600/20 px-6">
        <div className="container mx-auto flex gap-1">
          <button
            onClick={() => setActiveTab('bracket')}
            className={`flex items-center gap-2 px-6 py-3 font-medium transition-all border-b-2 -mb-px ${
              activeTab === 'bracket'
                ? 'text-white border-kod-orange'
                : 'text-white/50 hover:text-white/70 border-transparent'
            }`}
          >
            <LayoutGrid size={18} />
            Bracket
          </button>
          <button
            onClick={() => setActiveTab('leaderboard')}
            className={`flex items-center gap-2 px-6 py-3 font-medium transition-all border-b-2 -mb-px ${
              activeTab === 'leaderboard'
                ? 'text-white border-kod-orange'
                : 'text-white/50 hover:text-white/70 border-transparent'
            }`}
          >
            <Trophy size={18} />
            Leaderboard
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'bracket' ? (
          <TournamentBracket
            tournamentId={tournament.id}
            tournamentStatus={tournament.status}
            currentUserId={user?.id}
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
            registrationCloseDate={tournament.registration_close_date}
            registrationLocked={tournament.registration_locked}
          />
        ) : (
          <TournamentLeaderboard
            entries={leaderboardEntries}
            currentUserId={user?.id}
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
            isSharedView={true}
          />
        )}
      </div>

      {/* Default Footer */}
      <div className="border-t border-kod-purple-600/30 py-3 px-6 text-center bg-kod-purple-900/80">
        <p className="text-white/50 text-sm">
          © 2025 The Singing League - All Rights Reserved
        </p>
      </div>
    </div>
  );
};

export default SharedBracket;
